import React, { ReactElement } from 'react';

import AntLayout from 'antd/es/layout';
import { Button, ConfigProvider, Space, theme } from 'antd';
import styles from './footer.module.css';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

export interface FanGroupCreationFooterProps {
  children?: ReactElement;
  isDisabled?: boolean;
  isLoading?: boolean;
  handleNextStep?: () => void;
  handlePreviousStep?: () => void;
  currentStep: number;
  formSteps: string[];
  className?: string;
}
const { Footer: AntFooter } = AntLayout;

export function FanGroupCreationFooter({
  isDisabled,
  isLoading,
  children,
  handleNextStep,
  handlePreviousStep,
  currentStep,
  formSteps,
  className,
}: FanGroupCreationFooterProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === formSteps.length - 1;
  return (
    <AntFooter
      className={cx(styles.footer, className)}
      style={{
        borderTop: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Space className={children && styles.space}>
        {children}
        <div className={styles.controls}>
          {isFirstStep ? (
            <ConfigProvider
              theme={{
                components: {
                  Button: {
                    defaultActiveBorderColor: token.colorError,
                    defaultActiveColor: token.colorError,
                    defaultColor: token.colorError,
                    defaultHoverBorderColor: token.colorError,
                    defaultHoverColor: token.colorError,
                  },
                },
                token: {
                  colorPrimaryHover: token.colorError,
                  colorTextBase: token.colorError,
                  colorBorder: token.colorError,
                  colorPrimaryActive: token.colorError,
                  colorPrimary: token.colorError,
                },
              }}
            >
              <NavLink to="..">
                <Button
                  data-testid="fanGroupCreationCancelButton"
                  size="middle"
                  type="text"
                  icon={<CloseOutlined rev={undefined} />}
                >
                  {t('cancel')}
                </Button>
              </NavLink>
            </ConfigProvider>
          ) : (
            handlePreviousStep && (
              <Button
                data-testid="fanGroupCreationPreviousButton"
                size="middle"
                type="default"
                disabled={isDisabled}
                loading={isLoading}
                onClick={handlePreviousStep}
                icon={<LeftOutlined rev={undefined} />}
              >
                {t('prev_step_button_text')}
              </Button>
            )
          )}

          {!isLastStep && (
            <Button
              data-testid="fanGroupCreationSaveDraftButton"
              type="default"
              size="middle"
              htmlType="submit"
              disabled={isDisabled}
              loading={isLoading}
            >
              {t('save_draft')}
            </Button>
          )}

          <Button
            data-testid="fanGroupCreationNextButton"
            data-is-last-step={isLastStep}
            type="primary"
            disabled={isDisabled}
            loading={isLoading}
            htmlType={isLastStep ? 'submit' : 'button'}
            onClick={handleNextStep && handleNextStep}
          >
            {isLastStep ? t('general_finish-btn') : t('next_step_button_text')}
            <RightOutlined rev={undefined} />
          </Button>
        </div>
      </Space>
    </AntFooter>
  );
}
